import { useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Bot, Send, User, Sparkles } from "lucide-react";

export default function AIAssistant() {
  const { api } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm your wealth management AI assistant powered by GPT-5.2. I can help you with:\n\n• Portfolio analysis and recommendations\n• Real estate investment advice\n• Insurance policy guidance\n• Budget optimization\n• General financial planning\n\nHow can I assist you today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await api.post("/ai/chat", {
        query: userMessage,
        context: "general"
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (error) {
      toast.error("Failed to get AI response");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I encountered an error. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Layout title="AI Assistant">
      <div className="h-[calc(100vh-180px)] flex flex-col">
        {/* Chat messages */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none flex-1 overflow-hidden" data-testid="chat-container">
          <CardContent className="p-0 h-full flex flex-col">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${idx}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-violet-500" />
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] p-4 ${
                      msg.role === 'user'
                        ? 'bg-primary/20 border border-primary/30'
                        : 'bg-white/5 border border-white/10'
                    }`}
                  >
                    <p className="text-sm text-zinc-300 whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded bg-violet-500/20 flex items-center justify-center flex-shrink-0 ai-active">
                    <Sparkles className="w-5 h-5 text-violet-500 animate-pulse" />
                  </div>
                  <div className="bg-white/5 border border-violet-500/30 p-4">
                    <p className="text-sm text-zinc-400">Thinking...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Input area */}
            <div className="border-t border-white/10 p-4">
              <div className="flex gap-3">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about your finances, investments, or get advice..."
                  disabled={loading}
                  data-testid="chat-input"
                  className="bg-black/50 border-white/10 rounded-sm flex-1"
                />
                <Button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="bg-violet-500 text-white hover:bg-violet-600 rounded-sm px-6"
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-zinc-600 mt-2 text-center">
                Powered by GPT-5.2 • Your data is used to provide personalized advice
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
